<template>
  <section class="invitations-list">
    <section class="invitations-list-header">
      <invitations-list-filters
        class="invitations-list-filters"
        :scrollable="scrollable"
        :per-page="perPage"
      />

      <form-button
        v-if="filtering"
        class="invitations-list-filter"
        color="danger"
        icon="undo"
        @click="clear"
      >
        Limpar filtros
      </form-button>
    </section>

    <ul class="invitations-lists">
      <li
        v-for="invitation in list"
        :key="invitation.id"
      >
        <section class="invitations-list-link">
          <information-line :label="email(invitation)">
            <context-tag
              :color="invitation.invitation_accepted_at ? 'success' : 'waiting'"
              class="invitation-list-tag"
            >
              {{ invitation.invitation_accepted_at ? 'Já se cadastrou' : 'Ainda não se cadastrou' }}
            </context-tag>

            <form-button
              :color="invitation.invitation_accepted_at ? 'default' : 'danger'"
              class="invitation-list-button"
              size="small"
              @click="deleteOrProfile(invitation)"
            >
              {{ invitation.invitation_accepted_at ? 'Ir para o perfil' : 'Excluir convite' }}
            </form-button>
          </information-line>
        </section>

        <context-separator
          size="small"
          class="invitations-list-separator"
        />
      </li>

      <context-state
        :state="currentState"
        tag="li"
      >
        <template v-slot:loading>
          Carregando convites...
        </template>

        <template v-slot:notfound>
          Nenhum convite encontrado :(
        </template>

        <template v-slot:empty>
          Sem mais convites
        </template>
      </context-state>
    </ul>
  </section>
</template>

<script>
  import { mapState } from 'vuex';
  import mobile from '@sdk/helpers/interface/mobile';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import sort from '@sdk/views/Invitations/List/status';

   // eslint-disable-next-line import/no-extraneous-dependencies
  import { INITIAL_INVITATIONS_FILTERS as INITIAL } from '@/store/modules/user-invitations';

  import ContextState from '@cordel/components/Context/State.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';
  import ContextSeparator from '@cordel/components/Context/Separator.vue';

  import FormButton from '@cordel/components/Form/Button.vue';

  import InformationLine from '@cordel/components/Information/Line.vue';
  import InvitationsListFilters from '@sdk/views/Invitations/List/Filters.vue';

  export default {
    name: 'InvitationsList',
    components: {
      ContextState,
      ContextSeparator,
      ContextTag,

      FormButton,

      InformationLine,
      InvitationsListFilters,
    },
    mixins: [
      mobile,
    ],
    props: {
      perPage: {
        type: Number,
        default: INITIAL.per_page,
      },
      scrollable: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      ...mapState([
        'userInvitations',
      ]),

      filters() {
        return this.userInvitations.list.filters;
      },
      list() {
        return sort(this.userInvitations.list.items, this.filters.sort);
      },
      loading() {
        return this.userInvitations.list.loading;
      },
      currentState() {
        if (this.loading) {
          return 'loading';
        }

        if (this.list.length === 0) {
          return 'notfound';
        }

        if (this.scrollable) {
          return 'empty';
        }

        return null;
      },

      filtering() {
        return Object
          .keys(INITIAL)
          .some(key => INITIAL[key] !== this.userInvitations.list.filters[key]);
      },
    },
    beforeMount() {
      this.$store.dispatch('userInvitations/clear', { action: 'list' });
    },
    methods: {
      email(invitation) {
        return `${invitation.email}`;
      },
      deleteOrProfile(invitation) {
        if (invitation.invitation_accepted_at) {
          return this.$router.replace({
            name: 'users-read',
            params: {
              id: invitation.id,
            },
          });
        }

        return this.$store.dispatch('userInvitations/delete', {
          id: invitation.id,
        });
      },
      clear() {
        this.$store.dispatch('userInvitations/filter', {
          ...INITIAL,
        }).then(this.refresh);
      },
      refresh() {
        this.$store.dispatch('userInvitations/list', this.userInvitations.list.filters);
      },
    },
  };
</script>

<style lang="scss">
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .invitations-list-filters {
    .cordel-section-item {
      margin-top: var(--cordel-spacing-mini);

      &:first-child, &:nth-child(2) {
        margin-top: var(--cordel-spacing-mini);
      }

      @media all and (max-width: $cordel-breakpoint-mobile) {
        margin-top: var(--cordel-spacing-xs);

        &:first-child {
          margin-top: 0;
        }

        &:nth-child(2) {
          margin-top: var(--cordel-spacing-s);
        }
      }
    }
  }

  .invitations-list-link {
    .cordel-information-line {
      @media all and (max-width: $cordel-breakpoint-mobile) {
        flex-direction: column;

        &.variant--centered {
          align-items: flex-start;
        }
      }
    }

    .cordel-information-line-label {
      @media all and (max-width: $cordel-breakpoint-mobile) {
        line-height: var(--cordel-spacing-xl);
        margin-left: var(--cordel-spacing-micro);
      }
    }

    .cordel-information-line-value {
      justify-content: space-between;
      margin-left: var(--cordel-spacing-xxs);

      @media all and (max-width: $cordel-breakpoint-mobile) {
        flex-direction: column;
        width: 100%;
        align-items: flex-start;
      }
    }

    .cordel-button-group {
      @media all and (max-width: $cordel-breakpoint-mobile) {
        width: 100%;
      }
    }
  }

  .invitations-list-header {
    .cordel-sections {
      margin: 0;
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .invitations-list {
    margin-top: var(--cordel-spacing-blaster);
  }

  .invitations-lists {
    list-style: none;
    margin-top: var(--cordel-spacing-xl);
    overflow: hidden;
  }

  .invitations-list-link {
    display: block;
    padding: var(--cordel-spacing-xs) 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xxs) 0;
    }
  }

  .invitations-list-separator {
    background-color: var(--cordel-color-neutral-primary);
    height: var(--cordel-border-width-soft);
    margin: var(--cordel-spacing-xs) 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-spacing-xxs) 0;
    }
  }

  .invitation-list-tag {
    margin-left: calc(-1 * var(--cordel-spacing-micro));
  }

  .invitation-list-button {
    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-spacing-xxxs) 0
              calc(-1 * var(--cordel-spacing-xxxs)) calc(-1 * var(--cordel-spacing-xxs));
      width: 100%;
    }
  }

  .invitations-list-header {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: var(--cordel-spacing-s);
    justify-content: center;
    margin: 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      align-items: stretch;
      flex-direction: column;
      margin: 0;
    }
  }

  .invitations-list-filters {
    flex: 1;
  }
</style>
